import React, { useEffect, useRef } from "react";
type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  grow: number;
  life: number;
  maxLife: number;
};

// Emitters in fractions of the grader image box: blade and rear tandem wheels.
const EMITTERS = [{
  x: 0.56,
  y: 0.9,
  weight: 0.45
}, {
  x: 0.81,
  y: 0.88,
  weight: 0.3
}, {
  x: 0.92,
  y: 0.88,
  weight: 0.25
}];

// Canvas extends beyond the grader box (see className) so dust can trail behind.
const SCALE_X = 1.4;
const SCALE_Y = 1.2;
export function GraderDust() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    const isSmall = window.matchMedia("(max-width: 768px)").matches;
    const spawnPerSecond = isSmall ? 5 : 11;
    const maxParticles = isSmall ? 28 : 60;
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    let width = 0;
    let height = 0;
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    const particles: Particle[] = [];
    let spawnDebt = 0;
    let last = performance.now();
    let frame = 0;
    const pickEmitter = () => {
      let roll = Math.random();
      for (const e of EMITTERS) {
        if ((roll -= e.weight) <= 0) return e;
      }
      return EMITTERS[0];
    };
    const tick = (time: number) => {
      const dt = Math.min(0.05, (time - last) / 1000);
      last = time;
      const graderW = width / SCALE_X;
      const graderH = height / SCALE_Y;
      spawnDebt += dt * spawnPerSecond;
      while (spawnDebt >= 1 && particles.length < maxParticles) {
        spawnDebt -= 1;
        const e = pickEmitter();
        particles.push({
          x: e.x * graderW + (Math.random() - 0.5) * graderW * 0.05,
          y: e.y * graderH + (Math.random() - 0.5) * graderH * 0.03,
          vx: graderW * (0.05 + Math.random() * 0.07),
          vy: -graderH * (0.02 + Math.random() * 0.05),
          r: graderW * (0.015 + Math.random() * 0.02),
          grow: graderW * (0.03 + Math.random() * 0.03),
          life: 0,
          maxLife: 2.2 + Math.random() * 1.8
        });
      }
      if (spawnDebt > 1) spawnDebt = 1;
      ctx.clearRect(0, 0, width, height);
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.life += dt;
        if (p.life >= p.maxLife) {
          particles.splice(i, 1);
          continue;
        }
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vx *= 0.985;
        p.r += p.grow * dt;
        const t = p.life / p.maxLife;
        const alpha = (t < 0.15 ? t / 0.15 : 1 - (t - 0.15) / 0.85) * 0.22;
        const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r);
        g.addColorStop(0, `rgba(214, 170, 120, ${alpha})`);
        g.addColorStop(1, "rgba(214, 170, 120, 0)");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);
  return <canvas ref={canvasRef} aria-hidden="true" className="pointer-events-none absolute left-0 top-[-10%] h-[120%] w-[140%]" data-id="element-20" data-mp="3imlxom5tmhrq" />;
}